/**
 * 评论「更多」菜单要用的几个判定，菜单、分享弹窗和评论列表共用。
 *
 * 口径与 App 端长按评论弹出的操作面板一致（pp-front-uniapp 的 pages/article/index.vue：
 * commentMenuGroups / commentCollectStickerUrl / buildCommentShareContent），
 * 改这里要同步看一眼那边，否则两端同一条评论能做的事不一样。
 */
import { isAnonymous } from '@/constant'

// 账号状态：2 = 已注销，见后端 UserStatusEnum
const USER_STATUS_CANCELLED = 2

/**
 * 是不是我发的评论（含我名下的匿名身份）。
 *
 * 字段叫 canDelete 是历史原因，服务端下发的实际含义就是「这是我的评论」，
 * 老版本 App 也是这么读的，所以语义不会变。帖子作者删别人评论走 canDeleteAsArticleAuthor。
 */
export function isMyComment (comment) {
  return !!(comment && comment.canDelete)
}

/**
 * 能不能删：自己的评论，或者我是这篇帖子的作者（可以删帖子下任何人的评论）。
 * 两个字段都由服务端按登录人的全部身份（含匿名）算好，删除接口的权限判定与之一致。
 */
export function canDeleteComment (comment) {
  return isMyComment(comment) || !!(comment && comment.canDeleteAsArticleAuthor)
}

export function isAnonymousComment (comment) {
  return !!comment && comment.nickType === isAnonymous.YES
}

export function isCancelledCommentUser (comment) {
  return !!comment && (comment.cancelled === true || comment.userStatus === USER_STATUS_CANCELLED)
}

/**
 * 能不能私信评论人：匿名评论拿不到真实用户，已注销的人也没法聊，自己的评论没必要。
 */
export function canChatWithCommentUser (comment) {
  return !!comment && !isMyComment(comment) && !isAnonymousComment(comment) && !isCancelledCommentUser(comment)
}

/**
 * 「添加到表情」能收的那张图：评论带了斗图就是斗图，否则只有「恰好一张配图」时才算。
 * 多图评论里收哪一张说不清，App 端同样不给这一项。
 */
export function collectableStickerUrl (comment) {
  if (!comment) return ''
  if (comment.stickerUrl) return comment.stickerUrl
  const images = Array.isArray(comment.imageList) ? comment.imageList.filter(Boolean) : []
  return images.length === 1 ? images[0] : ''
}

/**
 * 评论的一句话摘要，给分享卡片和举报弹窗回显用。
 * 纯图 / 纯表情评论没有正文，给个占位，不然卡片上是空白一行。
 *
 * @param {Object} comment
 * @param {Number} maxLength 超过就截断并补省略号
 */
export function commentSummary (comment, maxLength) {
  if (!comment) return ''
  const text = String(comment.content || '').replace(/\s+/g, ' ').trim()
  if (text) {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }
  if (comment.stickerUrl) return '[表情]'
  const images = Array.isArray(comment.imageList) ? comment.imageList.filter(Boolean) : []
  return images.length ? '[图片]' : ''
}

/**
 * 「分享给好友」发出去的卡片内容（messageType = 5）。
 *
 * 字段与 App 端 buildCommentShareContent 一致：沿用帖子分享卡片的结构，
 * 多带 type='comment' 和 commentId。两端的私信页都认得这种卡片，
 * 点开会进帖子详情并定位到这条评论；认不出 type 的老版本退化成普通帖子卡片，不会白屏。
 * 唯一的差别是纯图 / 纯表情评论：App 端 content 发空串，这里发「[图片]」「[表情]」占位。
 * 匿名评论只带匿名昵称和头像，不暴露真实身份。
 */
export function buildCommentShareContent (comment, articleId) {
  return JSON.stringify({
    type: 'comment',
    articleId: String(articleId || comment.articleId || ''),
    commentId: String(comment.id),
    authorAvatar: comment.avatar || '',
    authorName: comment.nickname || '',
    content: commentSummary(comment, 30)
  })
}
